import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Save, Loader2, Copy } from 'lucide-react';
import { adminApi } from '../../api';
import { PageHeader, Table, Modal, Field, Input, Select } from './AdminUI';

const EMPTY = { email: '', password: '', display_name: '', activity_ids: [], is_active: true, is_demo: false };

export default function AdminSponsorUsers() {
  const [items, setItems] = useState([]);
  const [activities, setActivities] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);

  const load = () => { setLoading(true); adminApi.sponsorUsers().then(setItems).finally(() => setLoading(false)); };
  useEffect(() => { load(); adminApi.activities().then(setActivities); }, []);

  const filtered = q ? items.filter(u => (u.email + ' ' + (u.display_name || '')).toLowerCase().includes(q.toLowerCase())) : items;

  const save = async (e) => {
    e.preventDefault();
    const payload = {...editing};
    if (editing.id) {
      const patch = { activity_ids: payload.activity_ids, display_name: payload.display_name, is_active: payload.is_active };
      if (payload.password) patch.password = payload.password;
      await adminApi.updateSponsorUser(editing.id, patch);
    } else {
      await adminApi.createSponsorUser(payload);
    }
    setEditing(null); load();
  };

  const del = async (u) => { if (!window.confirm(`Elimina sponsor ${u.email}?`)) return; await adminApi.deleteSponsorUser(u.id); load(); };
  const resetPwd = async (u) => {
    const p = window.prompt('Nuova password (min 6 caratteri):', 'yup' + Math.floor(Math.random()*10000));
    if (!p || p.length < 6) return;
    await adminApi.updateSponsorUser(u.id, { password: p });
    alert(`Password aggiornata per ${u.email}\nNuova password: ${p}`);
  };
  const toggleActive = async (u) => { await adminApi.updateSponsorUser(u.id, { is_active: !u.is_active }); load(); };

  const toggleActivity = (aid) => {
    const set = new Set(editing.activity_ids || []);
    set.has(aid) ? set.delete(aid) : set.add(aid);
    setEditing({...editing, activity_ids: Array.from(set)});
  };

  return (
    <div>
      <PageHeader title="Account Sponsor" subtitle={`${items.length} sponsor registrati — gestisci accessi, password e attività collegate`}
        action={<button onClick={() => setEditing({...EMPTY})} className="btn-primary flex items-center gap-2" data-testid="admin-sponsor-new-btn"><Plus size={16}/> Nuovo sponsor</button>}
      />
      <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Cerca per email o nome..." className="mb-4 max-w-md"/>
      {loading ? <div className="text-center py-8"><Loader2 className="inline animate-spin" size={20}/></div> : (
        <Table
          columns={[
            { key: 'email', label: 'Email', render: u => (
              <div>
                <div className="font-semibold flex items-center gap-2 flex-wrap">
                  {u.email}
                  {u.is_demo && <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[10px] font-bold">DEMO</span>}
                  {!u.is_active && <span className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 text-[10px] font-bold">DISATTIVATO</span>}
                </div>
                {u.display_name && <div className="text-xs text-gray-500">{u.display_name}</div>}
              </div>
            )},
            { key: 'activity_ids', label: 'Attività collegate', render: u => (
              <div className="text-xs text-gray-600">{(u.activity_ids || []).length} attività</div>
            )},
            { key: 'last_login', label: 'Ultimo accesso', render: u => u.last_login ? new Date(u.last_login).toLocaleString('it-IT') : 'mai' },
          ]}
          rows={filtered}
          actions={u => (
            <div className="flex gap-1 justify-end">
              <button onClick={() => setEditing({...u, password: ''})} className="p-1.5 hover:bg-gray-100 rounded text-blue-600" title="Modifica"><Edit2 size={15}/></button>
              <button onClick={() => resetPwd(u)} className="p-1.5 hover:bg-amber-100 rounded text-amber-700" title="Reset password"><Copy size={15}/></button>
              <button onClick={() => toggleActive(u)} className="p-1.5 hover:bg-gray-100 rounded text-gray-700 text-xs" title={u.is_active ? 'Disattiva' : 'Attiva'}>{u.is_active ? '⏸' : '▶'}</button>
              {!u.is_demo && <button onClick={() => del(u)} className="p-1.5 hover:bg-red-100 rounded text-red-600"><Trash2 size={15}/></button>}
            </div>
          )}
        />
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Modifica sponsor' : 'Nuovo sponsor'} maxWidth="max-w-2xl">
        {editing && (
          <form onSubmit={save} className="space-y-3">
            <Field label="Email *"><Input type="email" required value={editing.email} onChange={e => setEditing({...editing, email: e.target.value.toLowerCase()})} disabled={!!editing.id}/></Field>
            <Field label={editing.id ? 'Nuova password (lascia vuoto per non cambiarla)' : 'Password *'}>
              <Input type="text" value={editing.password || ''} onChange={e => setEditing({...editing, password: e.target.value})} required={!editing.id}/>
            </Field>
            <Field label="Nome visualizzato"><Input value={editing.display_name || ''} onChange={e => setEditing({...editing, display_name: e.target.value})} placeholder="es. Trattoria Bella Vista"/></Field>

            <Field label="Attività collegate">
              <div className="max-h-64 overflow-y-auto border rounded-lg p-2 space-y-1">
                {activities.map(a => (
                  <label key={a.id} className="flex items-center gap-2 text-sm py-1 hover:bg-gray-50 px-2 rounded cursor-pointer">
                    <input type="checkbox" checked={(editing.activity_ids || []).includes(a.id)} onChange={() => toggleActivity(a.id)}/>
                    <span className="flex-1 truncate">{a.name}</span>
                    <span className="text-xs text-gray-500">{a.city_slug}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">Lo sponsor potrà vedere/modificare SOLO le attività selezionate.</p>
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.is_active} onChange={e => setEditing({...editing, is_active: e.target.checked})}/> Attivo
              </label>
              {!editing.id && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editing.is_demo} onChange={e => setEditing({...editing, is_demo: e.target.checked})}/> Account demo (reset ogni 24h)
                </label>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-300 rounded-md">Annulla</button>
              <button type="submit" className="btn-primary flex items-center gap-2"><Save size={16}/> Salva</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
